import { useWindowEvent } from '@/hooks/use-event'
import Vue, { h, ref } from 'vue'

type Fn<T = any> = (...args: T[]) => any

interface Props<T> {
  modalComponent: Vue.Component
  confirmCallback?: Fn<T>
  cancelCallback?: Fn
  [key: string]: any
  isUseKeyboard?: boolean
}
interface ReturnPromiseType<T> {
  status: boolean
  data: T
}

// 缓存弹窗
const modalCache = new Map<string, Promise<ReturnPromiseType<any>>>()

// 用于同步弹窗
export const showSyncModal = <T = any>(
  options: Props<T>
): Promise<ReturnPromiseType<T>> => {
  const { modalComponent, confirmCallback, cancelCallback } = options

  // 使用 Vue 的标准方式获取组件名称
  const componentName =
    (modalComponent as any).name || (modalComponent as any).__name || 'unnamed'

  if (modalCache.has(componentName)) {
    return modalCache.get(componentName) as Promise<ReturnPromiseType<T>>
  }

  return new Promise((resolve) => {
    const ModalConstructor = Vue.extend({
      props: {
        visible: Boolean,
      },
      setup(props, ctx) {
        const visible = ref(true)
        const isDestroyed = ref(false)

        const destroyCom = () => {
          if (!isDestroyed.value) {
            isDestroyed.value = true
            visible.value = false
            instance.$destroy()
            if (document.body.contains(container)) {
              document.body.removeChild(container)
            }
          }
        }

        const handleOk = (...args: any[]) => {
          confirmCallback && confirmCallback()
          resolve({
            status: true,
            data: args.length === 1 ? args[0] : args,
          })
          destroyCom()
        }

        const handleCancel = (...args: any[]) => {
          cancelCallback && cancelCallback()
          resolve({
            status: false,
            data: args.length === 1 ? args[0] : args,
          })
          destroyCom()
        }
        if (options.isUseKeyboard) {
          useWindowEvent(
            'keydown',
            (e) => {
              if (!visible.value) return
              if (e.key === 'Enter') {
                handleOk()
              }
              if (e.key === 'Escape') {
                handleCancel()
              }
            },
            {
              capture: true,
            }
          )
        }

        return () =>
          h(modalComponent, {
            props: {
              ...options,
              visible: visible.value,
            },
            on: {
              'update:visible': (val: boolean) => {
                visible.value = val
              },
              ok: handleOk,
              cancel: handleCancel,
            },
          })
      },
    })

    const container = document.createElement('div')
    document.body.appendChild(container)
    const instance = new ModalConstructor()
    instance.$mount(container)
  })
}
